// src/pages/app-home.ts
import { LitElement, css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { resolveRouterPath } from '../router';

import '../components/icons/font-awesome';
import '../components/icons/mtg-symbols';
import "../components/header";
import "../components/navigation";

import { sharedStyles } from '../styles/shared-styles';
import { waStyles } from '../styles/wa-styles';
import { ribbonStyles } from '../styles/ribbon-styles';
import { alertStyles } from '../styles/alert-styles';
import { homeStyles } from '../styles/home-styles';
import '@web.awesome.me/webawesome-pro/dist/components/button/button.js';
import '@web.awesome.me/webawesome-pro/dist/components/card/card.js';
import '@web.awesome.me/webawesome-pro/dist/components/details/details.js';
import '@web.awesome.me/webawesome-pro/dist/components/icon/icon.js';

@customElement('app-home')
export class AppHome extends LitElement {

  @property() message = 'Welcome!';

  static styles = [
    sharedStyles,
    waStyles,
    ribbonStyles,
    alertStyles,
    homeStyles,
    css`
      wa-card {
        --spacing: 1.25rem;
      }
      .grid {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        grid-auto-rows: 1fr;
        gap: 2rem;
      }
      .grid wa-card,
      .flex wa-card {
        position: relative;
      }
      .grid wa-card,
      .flex wa-card {
        height: 100%;
      }
      .grid wa-card::part(body),
      .flex wa-card::part(body) {
        flex: 1 1 auto;
      }
      .grid wa-card > wa-button,
      .flex wa-card > wa-button {
        padding-top: 2rem;
        position: absolute;
        bottom: 1rem;
        left: 1rem;
        right: 1rem;
      }
        .dashboard-card { width: 100%; max-width: 250px;}
        .dashboard-card p {
        height: 100%;}
      wa-card::part(media) {
        background-color: var(--wa-color-surface-lowered);
        display: flex;
        justify-content: center;
      }
      wa-card.work-in-progress {
        border-color: var(--wa-color-warning-border-loud);
      }
      wa-card {
        background-color: transparent;
        height: 100%;
      }
      wa-card::part(body) {
        background-color: var(--wa-color-surface-raised);
        flex: 1 1 auto;
      }
      wa-card::part(footer) {
          padding: .75rem 1rem;
      }
      .gameplay-features {
        margin-top: .75rem;
        margin-bottom: 2rem;
      }
      .gameplay-features ul {
        margin-block-start: 0 !important;
        padding-inline-start: 1rem !important;
      }
      wa-details.gameplay-features::part(base) {
        background-color: transparent;
      }
      wa-details.gameplay-features::part(header) {
        padding: .5rem;
      }
  `];

  async firstUpdated() {
    // this method is a lifecycle even in lit
    // for more info check out the lit docs https://lit.dev/docs/components/lifecycle/
  }

  share() {
    if ((navigator as any).share) {
      (navigator as any).share({
        title: 'Moonsilver Waypoints',
        text: 'Tracking games of Magic: The Gathering and other TCG scores.',
        url: 'https://www.adamjolicoeur.me',
      });
    }
  }

  render() {
    return html`
      <!-- <app-header ?enableShare="${true}" ?enableAbout=${true}></app-header> -->
      <main style="margin-bottom: 2.5rem;">
        <h1>Moonsilver Waypoints</h1>
        <content class="main">
          <h2>Select a gameplay method to get started</h2>
          <p>
            Click on the "features" button of each item to learn more.
          </p>
          <section style="
              display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: flex-start;
    align-items: flex-start;
    gap: 2rem;
    " class="flex">
            <wa-card class="dashboard-card">
              <img
                slot="media"
                src="/assets/svg/bcore.svg"
                alt="Image of a stack of cards"
                style="width: 50%; height: 10rem;"
              />
              <div slot="header"><strong>Tournament</strong></div>
              <wa-details summary="Features" class="gameplay-features">
              <wa-icon src="/assets/svg/dice-d20-solid.svg" slot="expand-icon"></wa-icon>
              <wa-icon src="/assets/svg/dice-d20-solid.svg" slot="collapse-icon" style="rotate: 180deg;"></wa-icon>
                <p>
                Running a tournament? Swiss rounds are paired by record, and standings follow the official Magic tiebreakers.
                </p>
                <ul>
                  <li>Automatic Swiss pairings with byes, drops, and late entries</li>
                  <li>One-tap match results with game scores</li>
                  <li>Standings ranked by points, opponents' match win %, game win %, and opponents' game win %</li>
                  <li>Top 4 or top 8 single elimination bracket</li>
                  <li>Round timer, projector display, and printable pairings</li>
                  <li>Round history with corrections</li>
                  <li>CSV export and JSON backup and restore</li>
                </ul>
              </wa-details>
              <br />
              <wa-button variant="brand" pill href="${resolveRouterPath('game-tracker')}">Start</wa-button>
            </wa-card>
            <wa-card class="dashboard-card">
              <img
                slot="media"
                src="/assets/svg/cmd.svg"
                alt="Commander logo"
                style="width: 50%; height: 10rem;"
              />
              <div slot="header"><strong>Commander</strong></div>
              <wa-details summary="Features" class="gameplay-features">
              <wa-icon src="/assets/svg/dice-d20-solid.svg" slot="expand-icon"></wa-icon>
              <wa-icon src="/assets/svg/dice-d20-solid.svg" slot="collapse-icon" style="rotate: 180deg;"></wa-icon>
                <p>
                For use with 4 player games, complete with a results table, standings, match log, and personalization.
                </p>
                <ul>
                  <li>Custom player name entry</li>
                  <li>Track Wins, Losses, and Draws</li>
                  <li>Match log for each round</li>
                  <li>Export gameplay information to CSV</li>
                </ul>
              </wa-details>
              <br />
              <wa-button variant="brand" pill href="${resolveRouterPath('counter')}">Start</wa-button>
            </wa-card>
            <wa-card class="dashboard-card">
              <img
                slot="media"
                src="assets/svg/shield-halved-solid.svg"
                alt="Generic image"
                style="width: 50%; height: 10rem;"
              />
              <div slot="header"><strong>Match Play</strong></div>
              <wa-details summary="Features" class="gameplay-features">
              <wa-icon src="/assets/svg/dice-d20-solid.svg" slot="expand-icon"></wa-icon>
              <wa-icon src="/assets/svg/dice-d20-solid.svg" slot="collapse-icon" style="rotate: 180deg;"></wa-icon>
                <p>
                For those playing 1v1 games. Simple life counter that adapts to your device orientation for use on monitors, tablets, and phones.
                </p>
                <ul>
                  <li>Set starting life total</li>
                  <li>Simple +1 or -1 counter</li>
                  <li>Match log for each round</li>
                </ul>
              </wa-details>
              <br />
              <wa-button variant="brand" pill href="${resolveRouterPath('standard-tracker')}">Start</wa-button>
            </wa-card>
            <wa-card class="dashboard-card">
              <img
                slot="media"
                src="/assets/svg/dice-d20-solid.svg"
                alt="A die"
                style="width: 50%; height: 10rem;"
              />
              <div slot="header"><strong>Dice Roller</strong></div>
              <wa-details summary="Features" class="gameplay-features">
              <wa-icon src="/assets/svg/dice-d20-solid.svg" slot="expand-icon"></wa-icon>
              <wa-icon src="/assets/svg/dice-d20-solid.svg" slot="collapse-icon" style="rotate: 180deg;"></wa-icon>
                <p>
                A simple D20 dice roller with history.
                </p>
                <ul>
                  <li>Roll a D20</li>
                  <li>View roll history</li>
                </ul>
              </wa-details>
              <br />
              <wa-button variant="brand" pill href="${resolveRouterPath('roll-dice')}">Start</wa-button>
            </wa-card>
          </section>
        </content>
      </main>
      <app-navigation></app-navigation>
    `;
  }
}
